/**
 * v0.4 追加要件⑪ 章② (D-354): 時間帯区分の判定 (クライアント側)。
 * `apps/api/src/lib/shift-report/time-band.ts` と同じロジック (別デプロイのため複製、
 * `lib/shift-format.ts` と同型の判断)。
 *
 * ⚠️ 章②時点では時間帯の色分けはこのクライアント側ロジックのみで完結している
 * (`isoToLocalHm`、ブラウザローカル時刻を使う既存の単一TZ前提を踏襲、`lib/shift-format.ts` 参照)。
 * サーバー側にも同名の純関数 (`apps/api/src/lib/shift-report/time-band.ts`) を用意してあるが
 * 章②時点ではどの route からも呼ばれていない (設計 §3-1 が想定したノードTZでの判定は未実装、
 * 既知の制約として設計書に記録済み)。
 */
import { Sunrise, Sun, Sunset, Moon, MoonStar, Clock } from 'lucide-react';

export type TimeBand = {
  band_id: string;
  code: string;
  label: string;
  start_time: string;
  end_time: string;
  color_token: string | null;
  icon_key: string | null;
  sort_order: number;
};

const minutesOfDay = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** 'HH:MM' がバンドの [start, end) に入るか。start > end は日跨ぎとして扱う。 */
export const isWithinBand = (hhmm: string, band: Pick<TimeBand, 'start_time' | 'end_time'>): boolean => {
  const t = minutesOfDay(hhmm);
  const start = minutesOfDay(band.start_time);
  const end = minutesOfDay(band.end_time);
  if (start === end) return false;
  if (start < end) return t >= start && t < end;
  return t >= start || t < end;
};

/** sort_order 昇順で最初にマッチしたバンドを返す (該当なしは null)。 */
export const resolveTimeBand = (localStartHhmm: string, bands: TimeBand[]): TimeBand | null => {
  const sorted = [...bands].sort((a, b) => a.sort_order - b.sort_order);
  return sorted.find((b) => isWithinBand(localStartHhmm, b)) ?? null;
};

// ⚠️ apps/api/src/lib/shift-report/time-band.ts の TIME_BAND_COLOR_TOKENS と同じ許容値集合。
// chart.1〜3 (tailwind.config.js「データ可視化（グラフ・バー：紺の単色階調）」) は左端バー用に
// 設計された唯一の中立トークン群であり、brand.red/alert.*/misespark-orange・yellow と衝突しない (D-390)。
// ⚠️ design-expert High指摘: chart.4 (#B9C7DD) は白背景に対し実測コントラスト比 1.67:1 で
// WCAG 1.4.11 (非テキスト要素 3:1) 未達のため allowlist から除外した (chart.1=14.8:1 / chart.2=6.6:1 /
// chart.3=3.1:1 は基準を満たす)。
export const TIME_BAND_COLOR_TOKENS = ['chart-1', 'chart-2', 'chart-3'] as const;
export const TIME_BAND_COLOR_CLASSES: Record<string, string> = {
  'chart-1': 'bg-chart-1',
  'chart-2': 'bg-chart-2',
  'chart-3': 'bg-chart-3',
};
// design-expert Medium指摘: 設定画面の色 Select が内部トークン名をそのまま表示しないよう、
// アイコンの TIME_BAND_ICON_LABELS と同型の日本語ラベルを用意する。
export const TIME_BAND_COLOR_LABELS: Record<string, string> = {
  'chart-1': '紺 (濃)', 'chart-2': '紺 (中)', 'chart-3': '紺 (淡)',
};

// apps/api/src/lib/shift-report/time-band.ts の TIME_BAND_ICON_KEYS と同じ許容値集合。
export const TIME_BAND_ICON_KEYS = ['sunrise', 'sun', 'sunset', 'moon', 'moon-star', 'clock'] as const;
export const TIME_BAND_ICON_LABELS: Record<string, string> = {
  sunrise: '朝日 (早朝)', sun: '太陽 (日中)', sunset: '夕日 (夕方)', moon: '月 (夜)', 'moon-star': '月と星 (深夜)', clock: '時計 (汎用)',
};

// design-and-code Low指摘: icon_key → lucide コンポーネントの対応表が ShiftBoardPage.tsx /
// ShiftPatternsSettingsPage.tsx に独立して2重定義されていたため、ここへ一本化する (§8-55対策)。
export const TIME_BAND_ICON_COMPONENTS: Record<string, typeof Sunrise> = {
  sunrise: Sunrise, sun: Sun, sunset: Sunset, moon: Moon, 'moon-star': MoonStar, clock: Clock,
};
